import { GOOGLE_ANALYTICS_ID } from "@/config/legal";

const SCRIPT_ID = "guia-emprendedor-google-analytics";
const CONSENT_STORAGE_KEY = "ef_consent_v2";
const ACQUISITION_STORAGE_KEY = "ef_acquisition_v1";
const ANALYTICS_CLIENT_ID_STORAGE_KEY = "ef_analytics_client_id_v1";
const EVENT_CALLBACK_TIMEOUT_MS = 2_000;
const CLIENT_ID_TIMEOUT_MS = 250;
const ANALYTICS_EVENTS = new Set([
  "begin_checkout",
  "checkout_click",
  "diagnosis_complete",
  "diagnosis_cta_click",
  "diagnosis_entry",
  "diagnosis_start",
  "plan_select",
  "plan_unlock_click",
  "public_cta_click",
  "sign_up",
  "tool_complete",
  "tool_start",
]);
const CAMPAIGN_PARAMETERS = Object.freeze({
  utm_id: "campaign_id",
  utm_source: "campaign_source",
  utm_medium: "campaign_medium",
  utm_campaign: "campaign_name",
  utm_term: "campaign_term",
  utm_content: "campaign_content",
});
const CHECKOUT_PRODUCTS = Object.freeze({
  plan_completo: { value: 15 },
  plan_plus: { value: 49 },
  upgrade_plus: { value: 10 },
});

// Google processes command objects created from `arguments`. Pushing a rest
// parameter array leaves commands visible in dataLayer but does not initialize
// gtag.js or send collection requests.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

const hasAnalyticsConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || "null")?.analytics === true;
  } catch {
    return false;
  }
};

const storedAcquisition = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ACQUISITION_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const cleanCampaignValue = (value) => value?.trim().slice(0, 100) || "";

export const campaignForSearch = (search = "") => {
  const query = new URLSearchParams(search);
  return Object.fromEntries(Object.entries(CAMPAIGN_PARAMETERS)
    .map(([queryName, eventName]) => [eventName, cleanCampaignValue(query.get(queryName))])
    .filter(([, value]) => value));
};

const safeCampaignQuery = (search = "") => {
  const input = new URLSearchParams(search);
  const output = new URLSearchParams();
  Object.keys(CAMPAIGN_PARAMETERS).forEach((key) => {
    const value = cleanCampaignValue(input.get(key));
    if (value) output.set(key, value);
  });
  const serialized = output.toString();
  return serialized ? `?${serialized}` : "";
};

const locationParts = (locationOrPath) => {
  if (typeof locationOrPath === "object" && locationOrPath) {
    return {
      pathname: locationOrPath.pathname || "/",
      search: locationOrPath.search || "",
    };
  }
  const pathname = locationOrPath || "/";
  return {
    pathname,
    search: typeof window !== "undefined" && pathname === window.location.pathname
      ? window.location.search
      : "",
  };
};

export const acquisitionForPath = (pathname) => {
  const article = pathname.match(/^\/(?:en\/)?blog\/([^/]+)$/)?.[1];
  if (article) return { source_type: "article", source_id: article };
  const tool = pathname.match(/^\/(?:herramientas|en\/tools)\/([^/]+)$/)?.[1];
  if (tool) return { source_type: "tool", source_id: tool };
  if (["/blog", "/en/blog"].includes(pathname)) return { source_type: "blog", source_id: "blog-index" };
  if (["/herramientas", "/en/tools"].includes(pathname)) return { source_type: "tools", source_id: "tools-index" };
  if (["/", "/en"].includes(pathname)) return { source_type: "landing", source_id: "home" };
  return null;
};

export const rememberAcquisitionSource = (locationOrPath) => {
  if (!hasAnalyticsConsent()) return null;
  const { pathname, search } = locationParts(locationOrPath);
  const previous = storedAcquisition();
  const source = previous || acquisitionForPath(pathname) || {};
  const next = { ...source, ...campaignForSearch(search) };
  if (!Object.keys(next).length) return null;
  try {
    sessionStorage.setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(next));
  } catch {}
  return next;
};

const acquisitionParams = () => storedAcquisition() || {};

const safeEventParams = (params) => Object.fromEntries(Object.entries(params || {})
  .filter(([key, value]) => /^[a-z][a-z0-9_]{0,39}$/.test(key)
    && ["string", "number", "boolean"].includes(typeof value)
    && (typeof value !== "number" || Number.isFinite(value)))
  .map(([key, value]) => [key, typeof value === "string" ? value.slice(0, 100) : value]));

const validClientId = (value) => typeof value === "string"
  && /^\d{1,20}\.\d{1,20}$/.test(value)
  && value.split(".").every((part) => Number(part) > 0)
  ? value
  : null;

const clientIdFromGoogleCookie = () => {
  try {
    const value = document.cookie.split("; ")
      .find((cookie) => cookie.startsWith("_ga="))
      ?.slice(4);
    return validClientId(value?.split(".").slice(-2).join("."));
  } catch {
    return null;
  }
};

export const rememberedGoogleAnalyticsClientId = () => {
  try {
    return validClientId(sessionStorage.getItem(ANALYTICS_CLIENT_ID_STORAGE_KEY))
      || clientIdFromGoogleCookie();
  } catch {
    return clientIdFromGoogleCookie();
  }
};

// Measurement Protocol needs the same browser client ID that gtag created in
// order to retain the consent and attribution settings for a server-confirmed
// purchase. A short timeout means that a slow or blocked analytics script never
// materially delays Checkout; in that case no server-side purchase is sent.
export const getGoogleAnalyticsClientId = () => {
  if (!GOOGLE_ANALYTICS_ID
    || !hasAnalyticsConsent()
    || window[`ga-disable-${GOOGLE_ANALYTICS_ID}`]) return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      const clientId = validClientId(value);
      if (clientId) {
        try { sessionStorage.setItem(ANALYTICS_CLIENT_ID_STORAGE_KEY, clientId); } catch {}
      }
      resolve(clientId);
    };
    const timeoutId = window.setTimeout(() => finish(null), CLIENT_ID_TIMEOUT_MS);
    gtag("get", GOOGLE_ANALYTICS_ID, "client_id", finish);
  });
};

export const checkoutAnalyticsParams = (lookupKey, checkoutSource) => {
  const product = CHECKOUT_PRODUCTS[lookupKey];
  if (!product) return null;
  return {
    plan_id: lookupKey,
    checkout_source: checkoutSource,
    currency: "EUR",
    value: product.value,
  };
};

export const trackPublicEvent = (eventName, params = {}) => {
  if (!GOOGLE_ANALYTICS_ID
    || !ANALYTICS_EVENTS.has(eventName)
    || !hasAnalyticsConsent()
    || window[`ga-disable-${GOOGLE_ANALYTICS_ID}`]) return false;
  const safeParams = safeEventParams(params);
  const ecommerce = eventName === "begin_checkout" && CHECKOUT_PRODUCTS[safeParams.plan_id]
    ? {
      items: [{
        item_id: safeParams.plan_id,
        item_name: safeParams.plan_id,
        price: CHECKOUT_PRODUCTS[safeParams.plan_id].value,
        quantity: 1,
      }],
    }
    : {};
  gtag("event", eventName, { ...acquisitionParams(), ...safeParams, ...ecommerce });
  return true;
};

// Use this for events immediately followed by a cross-origin navigation. The
// callback lets gtag finish dispatching before leaving the current page, while
// the timeout guarantees that analytics failures never block the user flow.
export const trackPublicEventThen = (eventName, params = {}, callback) => {
  const continueFlow = typeof callback === "function" ? callback : () => {};
  if (!GOOGLE_ANALYTICS_ID
    || !ANALYTICS_EVENTS.has(eventName)
    || !hasAnalyticsConsent()
    || window[`ga-disable-${GOOGLE_ANALYTICS_ID}`]) {
    continueFlow();
    return false;
  }

  const safeParams = safeEventParams(params);
  const ecommerce = eventName === "begin_checkout" && CHECKOUT_PRODUCTS[safeParams.plan_id]
    ? {
      items: [{
        item_id: safeParams.plan_id,
        item_name: safeParams.plan_id,
        price: CHECKOUT_PRODUCTS[safeParams.plan_id].value,
        quantity: 1,
      }],
    }
    : {};
  let continued = false;
  let timeoutId;
  const finish = () => {
    if (continued) return;
    continued = true;
    window.clearTimeout(timeoutId);
    continueFlow();
  };
  timeoutId = window.setTimeout(finish, EVENT_CALLBACK_TIMEOUT_MS);
  gtag("event", eventName, {
    ...acquisitionParams(),
    ...safeParams,
    ...ecommerce,
    event_callback: finish,
    event_timeout: EVENT_CALLBACK_TIMEOUT_MS,
  });
  return true;
};

export const enableGoogleAnalytics = () => {
  if (!GOOGLE_ANALYTICS_ID || typeof window === "undefined") return false;
  window[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = false;
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("set", "ads_data_redaction", true);
  gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("js", new Date());
  gtag("config", GOOGLE_ANALYTICS_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GOOGLE_ANALYTICS_ID)}`;
    document.head.appendChild(script);
  }
  return true;
};

const expireCookie = (name, domain = "") => {
  document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ""}`;
};

export const disableGoogleAnalytics = () => {
  if (!GOOGLE_ANALYTICS_ID || typeof window === "undefined") return;
  window[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = true;
  // Before the provider script exists there is nothing to notify, and queuing
  // an update before the later default command breaks the expected order.
  if (document.getElementById(SCRIPT_ID)) {
    gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
  const suffix = GOOGLE_ANALYTICS_ID.replace(/^G-/, "").replace(/-/g, "_");
  const cookieNames = ["_ga", "_gid", "_gat", `_ga_${suffix}`];
  const hostname = window.location.hostname;
  const parentDomain = hostname.split(".").length > 1 ? `.${hostname.split(".").slice(-2).join(".")}` : "";
  cookieNames.forEach((name) => {
    expireCookie(name);
    if (hostname) expireCookie(name, hostname);
    if (parentDomain) expireCookie(name, parentDomain);
  });
  try { sessionStorage.removeItem(ANALYTICS_CLIENT_ID_STORAGE_KEY); } catch {}
};

const contentParamsForPath = (pathname) => {
  const content = acquisitionForPath(pathname);
  if (!content) return {};
  const contentGroup = ["article", "blog"].includes(content.source_type)
    ? "blog"
    : ["tool", "tools"].includes(content.source_type) ? "tools" : "landing";
  return {
    content_group: contentGroup,
    page_type: content.source_type,
    content_id: content.source_id,
  };
};

export const trackPageView = (location) => {
  if (!GOOGLE_ANALYTICS_ID
    || !hasAnalyticsConsent()
    || window[`ga-disable-${GOOGLE_ANALYTICS_ID}`]) return false;
  // Only campaign query parameters are retained. Stripe session identifiers,
  // authentication actions and other operational values never reach GA.
  const safePath = `${location.pathname}${location.hash || ""}`;
  const safeLocation = `${window.location.origin}${location.pathname}${safeCampaignQuery(location.search)}${location.hash || ""}`;
  gtag("event", "page_view", {
    page_path: safePath,
    page_location: safeLocation,
    page_title: document.title,
    ...acquisitionParams(),
    ...contentParamsForPath(location.pathname),
  });
  return true;
};
